import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Header from './header';
import {
  Group,
  Content,
  TopContent,
  BottomContent,
  ProjectList,
  TitleText,
  LoadingState,
  EmptyBox,
  ShowHideText,
  RightDiv,
  BottomHeader,
  FilterDiv,
  InputArea,
  SearchArea
} from './styles';
import AppLayout from '../../../component/appLayout';
import {
  addInvitedMember,
  favoriteProject,
  getProjectListUserNotIn,
  updateProjectData
} from '../../../services/projectServices';
import {
  BUTTON_TYPE,
  CUSTOMER_MEMBER_ROLE,
  EMPTY_STATE_SIZE,
  ICON_BUTTON_SIZE,
  ICON_BUTTON_TYPE,
  MENU_OPTIONS,
  PROJECT_FILTER_TYPE
} from '../../../global/constants';
import { useHistory } from 'react-router-dom';
import CreateNewProjectModalV2 from '../../../component/modals/createNewProjectModal';
import Modal from '../../../component/modals/modal';
import ProjectAllLoading from '../../../component/loading/projectAllLoading';
import Favorite from './favorite';
import ProjectLoading from '../../../component/loading/projectLoading';
import { captureException } from '../../../services/logService';
import Button from '../../../component/button';
import { getUserWorkspacePreferenceData, updateUserWorkspacePreference } from '../../../helpers/firebaseHelper';
import DeleteModal from '../../../component/modals/deleteModal';
import { trackAnalyticActivity } from '../../../services/analyticsService';
import { PROJECT_ANALYTICS } from '../../../global/analyticsConstants';
import { Dropdown, DropdownItem } from '../../../component/dropdown';
import { rootStore } from '../../../mobx/rootStore';
import { ProjectModel } from '../../../mobx/models/project';
import { observer } from 'mobx-react-lite';
import UserPreferenceSingleton from '../../../helpers/userPreferenceSingleton';
import { syncProjects, syncTasksForProjects } from '../../../services/syncService';
import BottomSubNavigation from '../../../component/bottomNavigation/subNavigation';
import BottomNavigationItem from '../../../component/bottomNavigation/subNavigation/bottomNavigationItem';
import { useMobileDevice } from '../../../global/useMobile';
import useKeyboardVisibility from '../../../global/useKeyboard';
import ProjectListItem from './projectListItem';
import OtherProjectListItem from './otherProjectListItem';
import { ROUTES } from '../../../routes/urls';
import IconButton from '../../../component/button/iconButton';
import EmptyStateV2 from '../../../component/emptyState';
import ModalHeader from '../../../component/modals/modalHeader';
import { MoreMenuItems } from '../../../global/row';

type ModalState =
  | { type: 'edit'; project: ProjectModel }
  | { type: 'archive'; projectId: string }
  | { type: 'confirmClose' }
  | null;

const AllProjects: React.FC = () => {
  // States
  const [isSearch, setIsSearch] = useState(false);
  const [isOtherProjectsVisible, setIsOtherProjectsVisible] = useState(false);
  const [activeModal, setActiveModal] = useState<ModalState>(null);
  const [isJoiningProjectId, setIsJoiningProjectId] = useState<string | null>(null);

  //mobx store variables
  const { projectStore, workspaceStore } = rootStore;
  const { currentWorkspace: workspace } = workspaceStore;
  const workspace_id = workspace?.id;
  const { projects, favoriteProjects, query, filteredProjects, projectUserNotIn, loading } = projectStore;

  // Other variables
  const history = useHistory();
  const isMobile = useMobileDevice();
  const isKeyboardVisible = useKeyboardVisibility();

  const handleViewClick = () => {
    setIsOtherProjectsVisible(!isOtherProjectsVisible);
  };

  const handleFilterClick = useCallback(async (key: string | number) => {
    key = Number(key);
    if (key === PROJECT_FILTER_TYPE.ALPHABETIC) {
      rootStore.projectStore.setSortType(PROJECT_FILTER_TYPE.ALPHABETIC);
      // Update project sorting in workspace preference
      await updateUserWorkspacePreference({ project_sorting: PROJECT_FILTER_TYPE.ALPHABETIC });
    } else {
      rootStore.projectStore.setSortType(PROJECT_FILTER_TYPE.CREATED_DATE);
      // Update project sorting in workspace preference
      await updateUserWorkspacePreference({ project_sorting: PROJECT_FILTER_TYPE.CREATED_DATE });
    }
  }, []);

  const handleSearch = useCallback((value: string) => {
    rootStore.projectStore.setQuery(value);
  }, []);

  const loadData = useCallback(async () => {
    try {
      if (workspace_id) {
        const filterOptionLocal = (await getUserWorkspacePreferenceData('project_sorting')) || 1;
        rootStore.projectStore.setSortType(filterOptionLocal);
        let promises = [syncProjects()];
        if (workspace?.isAdmin || workspace?.isOwner) {
          promises.push(getProjectListUserNotIn());
        }
        await Promise.all(promises);
      }
    } catch (e) {
      captureException(e);
    }
  }, [workspace, workspace_id]);

  useEffect(() => {
    loadData();
    return () => {
      rootStore.projectStore.setQuery('');
    };
  }, [loadData]);

  const onClickFavorite = useCallback(async (e: React.SyntheticEvent, project: ProjectModel, value: boolean) => {
    try {
      e.stopPropagation();
      project.setFavorite(value);
      await favoriteProject(project?.id, value);
    } catch (e) {
      captureException(e);
    }
  }, []);

  const onClickProjectList = useCallback(
    (item: ProjectModel) => {
      rootStore.projectStore.setCurrentProject(item?.id);
      history.push(ROUTES.PROJECT_TASKS.replace(':id', item?.id || ''));
    },
    [history]
  );

  const navigateToCreateProject = useCallback(() => {
    history.push(ROUTES.CREATE_PROJECT);
  }, [history]);

  const Cancel = useCallback(() => {
    if (activeModal?.type === 'edit') {
      setActiveModal({ type: 'confirmClose' });
    } else {
      setActiveModal(null);
      rootStore.projectStore.createProjectInput.clear();
    }
  }, [activeModal]);

  const onSendMail = useCallback(() => {
    const ownerUser = workspace?.users?.find((user) => user?.isOwner === true);
    const newTab = window.open(`mailto:${ownerUser?.email}`, '_blank');
    if (newTab) {
      newTab.focus();
    } else {
      alert('Please allow pop-ups for this site to open in a new tab.');
    }
  }, [workspace?.users]);

  const onClickJoin = useCallback(
    async (e: React.SyntheticEvent, project: ProjectModel) => {
      try {
        e.stopPropagation();
        setIsJoiningProjectId(project.id);
        const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
        const payload = {
          userId: userDetails?.id,
          newUserId: userDetails?.id,
          companyId: workspace_id,
          projectId: project?.id
        };
        await addInvitedMember(payload, true, true);
        await syncTasksForProjects([project.id]);
        rootStore.projectStore.joinProject(project.id);
      } catch (error) {
        captureException(error);
      } finally {
        setIsJoiningProjectId(null);
      }
    },
    [workspace_id]
  );

  const handleMenuOptionClick = useCallback((key: number | string, project: ProjectModel) => {
    if (key === MENU_OPTIONS.EDIT) {
      rootStore.projectStore.createProjectInput.updateWith(project);
      setActiveModal({ type: 'edit', project });
    } else if (key === MENU_OPTIONS.ARCHIVE) {
      setActiveModal({ type: 'archive', projectId: project.id });
    }
  }, []);

  const closeEditProjectModal = useCallback(() => {
    setActiveModal(null);
  }, []);

  const closeConfirmationModal = () => {
    setActiveModal(null);
  };

  const onConfirmClick = useCallback(() => {
    setActiveModal(null);
    rootStore.projectStore.createProjectInput.clear();
  }, []);

  const onArchiveProject = useCallback(async () => {
    try {
      if (!workspace?.isAdmin) return rootStore.alertNotificationStore.setErrorMessage(`You can't archive project.`);
      const updatePayload = { isArchived: true };
      if (!activeModal || activeModal.type !== 'archive') return;
      const projectId = activeModal.projectId;
      if (!projectId) return;
      const result = await updateProjectData(projectId, updatePayload);
      if (result) {
        trackAnalyticActivity(PROJECT_ANALYTICS.DELETED);
        setActiveModal(null);
        rootStore.projectStore.removeProjects([projectId]);
        rootStore.projectStore.createProjectInput.clear();
        rootStore.alertNotificationStore.setSuccessMessage('Project archived successfully.');
      }
    } catch (e) {
      captureException(e);
    }
  }, [activeModal, workspace?.isAdmin]);

  const renderMoreMenuItems = useCallback(
    (I: ProjectModel) => {
      return MoreMenuItems.map((item) => (
        <DropdownItem
          key={item.key}
          label={item.label}
          onClick={() => handleMenuOptionClick(item.key, I)}
          tone={item.label === 'Archive' ? 'critical' : 'primary'}
        />
      ));
    },
    [handleMenuOptionClick]
  );

  const onClearSearch = useCallback(() => {
    setIsSearch(false);
    rootStore.projectStore.setQuery('');
  }, []);

  const renderFilterItems = useMemo(() => {
    const menuItems = [
      { key: PROJECT_FILTER_TYPE.ALPHABETIC, label: 'Alphabetic' },
      { key: PROJECT_FILTER_TYPE.CREATED_DATE, label: 'Created date' }
    ];
    return menuItems.map((item) => (
      <DropdownItem key={item.key} label={item.label} onClick={() => handleFilterClick(item.key)} />
    ));
  }, [handleFilterClick]);

  const renderMenuDropdown = useCallback(
    (item: ProjectModel) => {
      return (
        (workspace?.isAdmin || workspace?.isOwner) && (
          <Dropdown
            modalTitle='Manage Project'
            activeClassName='active'
            content={
              <IconButton
                iconName='three-dots-horizontal-icon'
                size={isMobile ? ICON_BUTTON_SIZE.LARGE : ICON_BUTTON_SIZE.SMALL}
                variant={ICON_BUTTON_TYPE.TERTIARY}
                className='more-icon'
              />
            }
            trigger='click'>
            {renderMoreMenuItems(item)}
          </Dropdown>
        )
      );
    },
    [workspace?.isAdmin, workspace?.isOwner, isMobile, renderMoreMenuItems]
  );

  const renderJoinButton = useCallback(
    (item: ProjectModel) => {
      return (
        <RightDiv>
          <Button
            title='Join'
            variant={BUTTON_TYPE.SECONDARY}
            isLoading={isJoiningProjectId === item?.id}
            onClick={(e: React.SyntheticEvent) => onClickJoin(e, item)}
            size='small'
          />
        </RightDiv>
      );
    },
    [isJoiningProjectId, onClickJoin]
  );

  //all project list items
  const projectListItems = useMemo(() => {
    return filteredProjects?.map((project) => {
      return (
        <ProjectListItem
          key={project?.id}
          item={project}
          isMobile={isMobile}
          onClickProjectList={onClickProjectList}
          onClickFavorite={onClickFavorite}
          renderMenuDropdown={renderMenuDropdown}
        />
      );
    });
  }, [filteredProjects, isMobile, renderMenuDropdown, onClickProjectList, onClickFavorite]);

  // Handle empty state logic for description, button title, and button action
  const emptyStateDescription =
    workspace?.customerRole === CUSTOMER_MEMBER_ROLE.PROJECT_ONLY ||
    workspace?.customerRole === CUSTOMER_MEMBER_ROLE.FULL_ACCESS
      ? `It appears that you haven't been invited to any projects yet. Please ask your vendor to add you to a project.`
      : !workspace?.isAdmin && !workspace?.isOwner
      ? `It appears that you haven't been invited to any projects yet.
                   Please ask your admin to add you to a project.`
      : 'You can create new project by clicking the button below.';

  const emptyStateButtonTitle =
    workspace?.customerRole === CUSTOMER_MEMBER_ROLE.PROJECT_ONLY ||
    workspace?.customerRole === CUSTOMER_MEMBER_ROLE.FULL_ACCESS
      ? 'Request Project Invitation'
      : workspace?.isAdmin || workspace?.isOwner
      ? 'New Project'
      : '';

  const emptyStateButtonAction =
    workspace?.customerRole === CUSTOMER_MEMBER_ROLE.PROJECT_ONLY ||
    workspace?.customerRole === CUSTOMER_MEMBER_ROLE.FULL_ACCESS
      ? onSendMail
      : workspace?.isAdmin || workspace?.isOwner
      ? navigateToCreateProject
      : undefined;

  return (
    <>
      <AppLayout>
        <Group className='desktop-content'>
          <Header workspaceDetails={workspace} />
          {loading && (!projects || projects?.length === 0) && (
            <LoadingState>
              <ProjectLoading />
              <ProjectAllLoading />
            </LoadingState>
          )}
          {!loading && (!projects || projects?.length === 0) && (
            <EmptyStateV2
              size={EMPTY_STATE_SIZE.MEDIUM}
              title={'You have no projects yet'}
              description={emptyStateDescription}
              buttonTitle={emptyStateButtonTitle}
              onClick={emptyStateButtonAction}
              isAlignedCenter={true}
              imageUrl='project_empty'
            />
          )}
          {projects?.length > 0 && (
            <Content isKeyboardVisible={isKeyboardVisible}>
              {favoriteProjects?.length > 0 && (
                <TopContent>
                  <TitleText>Favourite</TitleText>
                  <Favorite
                    favoriteProjects={favoriteProjects}
                    onClickProjectList={(project) => onClickProjectList(project)}
                    onClickFavorite={(e, project, value) => onClickFavorite(e, project, value)}
                  />
                </TopContent>
              )}
              <BottomContent>
                {projects?.length > 0 && favoriteProjects?.length > 0 && (
                  <BottomHeader>
                    <TitleText>Projects</TitleText>
                    <FilterDiv>
                      <SearchArea isSearch={isSearch}>
                        <IconButton
                          iconName='search-icon'
                          size={isSearch ? ICON_BUTTON_SIZE.SMALL : ICON_BUTTON_SIZE.MEDIUM}
                          variant={ICON_BUTTON_TYPE.TERTIARY}
                          onClick={() => setIsSearch(true)}
                          className={isSearch ? 'search-icon-active' : 'search-icon'}
                        />
                        {isSearch && (
                          <>
                            <InputArea
                              isSearch={isSearch}
                              value={query}
                              onChange={(e) => handleSearch(e.target.value)}
                              autoFocus
                            />
                            <IconButton
                              iconName='new-close-icon'
                              size={ICON_BUTTON_SIZE.EXTRA_SMALL}
                              variant={ICON_BUTTON_TYPE.TERTIARY}
                              onClick={onClearSearch}
                              className='search-close-icon'
                            />
                          </>
                        )}
                      </SearchArea>
                      <Dropdown
                        modalTitle='Sort by'
                        activeClassName='active'
                        isMinWidth={128}
                        content={
                          <IconButton
                            iconName='small-filter-updated-icon'
                            size={ICON_BUTTON_SIZE.MEDIUM}
                            variant={ICON_BUTTON_TYPE.TERTIARY}
                          />
                        }
                        trigger='click'>
                        {renderFilterItems}
                      </Dropdown>
                    </FilterDiv>
                  </BottomHeader>
                )}
                {projects && projects?.length > 0 && filteredProjects?.length > 0 && (
                  <ProjectList isOtherProjectList={projectUserNotIn && projectUserNotIn?.length === 0}>
                    {projectListItems}
                  </ProjectList>
                )}

                {/* Empty state for not found projects on search filter */}
                {filteredProjects && filteredProjects?.length === 0 && (
                  <EmptyBox>
                    <EmptyStateV2
                      size={EMPTY_STATE_SIZE.SMALL}
                      description='No Search Result Found'
                      imageUrl='no_project_found'
                    />
                  </EmptyBox>
                )}
                {projectUserNotIn && projectUserNotIn?.length > 0 && (
                  <ShowHideText isOtherProjectList={isOtherProjectsVisible}>
                    {`You're not part of ${projectUserNotIn?.length} other projects.`}
                    <span role='button' onClick={handleViewClick}>
                      {' '}
                      {isOtherProjectsVisible ? 'Hide them all' : 'View them all'}
                    </span>
                  </ShowHideText>
                )}

                {projectUserNotIn && projectUserNotIn?.length > 0 && isOtherProjectsVisible && (
                  <ProjectList isOtherProjectList={true}>
                    {projectUserNotIn?.map((projectItem) => (
                      <OtherProjectListItem
                        key={projectItem?.id}
                        item={projectItem}
                        isMobile={isMobile}
                        renderJoinButton={renderJoinButton}
                      />
                    ))}
                  </ProjectList>
                )}
              </BottomContent>
            </Content>
          )}
        </Group>
      </AppLayout>
      <Modal
        open={activeModal?.type === 'edit'}
        onClose={Cancel}
        maxWidth={'632px'}
        isResponsiveMaxWidth={true}
        overflow='visible'>
        <ModalHeader title='Edit Project' onClose={Cancel} />
        <CreateNewProjectModalV2
          handleCloseModal={closeEditProjectModal}
          onClickCancel={Cancel}
          loadProjectData={() => loadData()}
        />
      </Modal>
      <Modal
        open={activeModal?.type === 'archive' || activeModal?.type === 'confirmClose'}
        onClose={closeConfirmationModal}
        maxWidth={'334px'}>
        <DeleteModal
          isYes={activeModal?.type === 'archive' ? false : true}
          onClose={closeConfirmationModal}
          onOk={activeModal?.type === 'archive' ? onArchiveProject : onConfirmClick}
          modalTitle={activeModal?.type === 'archive' ? 'Archive project?' : 'Confirmation Model'}
          isArchive={activeModal?.type === 'archive'}
          description={
            activeModal?.type === 'archive'
              ? 'This project will be moved to archived. You can restore it anytime.'
              : 'Are you sure you want close this model?'
          }
          loading={activeModal?.type === 'archive' ? loading : false}
        />
      </Modal>
      <BottomSubNavigation
        rightElement={
          (workspace?.isOwner || workspace?.isAdmin) && (
            <BottomNavigationItem
              iconName='plus-icon'
              onClick={() => {
                history.push(ROUTES.CREATE_PROJECT);
              }}
            />
          )
        }
      />
    </>
  );
};

export default observer(AllProjects);
